import { format, subYears } from "date-fns";

import { type DealFinancialReturnsResponse } from "@vega-api/generated/types/dealFinancialReturnsResponse";
import { type DealReportingSnapshotItem } from "@vega-api/generated/types/dealReportingSnapshotItem";
import { type MetricTrendPoint } from "@vega-api/generated/types/metricTrendPoint";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { compareDatesDesc } from "@vega-core/utils/compareDates";

export const MetricKey = {
  Moic: "moic",
  Irr: "irr",
  Dpi: "dpi",
  Tvpi: "tvpi",
} as const;

export type MetricKey = (typeof MetricKey)[keyof typeof MetricKey];

export const TimeRange = {
  All: "all",
  OneYear: "1y",
} as const;

export type TimeRange = (typeof TimeRange)[keyof typeof TimeRange];

export function getMetricLabel(metric: MetricKey): string {
  const labels: Record<MetricKey, string> = {
    [MetricKey.Moic]: m.coInvestments_report_moicGross(),
    [MetricKey.Irr]: m.coInvestments_report_irrNet(),
    [MetricKey.Dpi]: m.coInvestments_report_dpi(),
    [MetricKey.Tvpi]: m.coInvestments_report_tvpi(),
  };

  return labels[metric];
}

export function formatMoic(value?: number | null): string {
  if (value == null) return EMPTY_CELL_VALUE;

  return `${value.toFixed(2)}x`;
}

export function formatIrr(value?: number | null): string {
  if (value == null) return EMPTY_CELL_VALUE;

  return `${value.toFixed(1)}%`;
}

export function formatDpi(value?: number | null): string {
  if (value == null) return EMPTY_CELL_VALUE;

  return `${value.toFixed(2)}x`;
}

export function formatTvpi(value?: number | null): string {
  if (value == null) return EMPTY_CELL_VALUE;

  return `${value.toFixed(2)}x`;
}

function signedPrefix(value: number): string {
  return value >= 0 ? "+" : "−";
}

export function formatRatioDelta(value: number): string {
  return `${signedPrefix(value)}${Math.abs(value).toFixed(2)}x`;
}

export function formatIrrDelta(value: number): string {
  return `${signedPrefix(value)}${Math.abs(value).toFixed(1)}pp`;
}

export function formatSignedPercent(value: number): string {
  return `${signedPrefix(value)}${Math.abs(value).toFixed(1)}%`;
}

export function formatMetricValue({
  key,
  value,
}: {
  key: MetricKey;
  value?: number | null;
}): string {
  switch (key) {
    case MetricKey.Moic:
      return formatMoic(value);
    case MetricKey.Irr:
      return formatIrr(value);
    case MetricKey.Dpi:
      return formatDpi(value);
    case MetricKey.Tvpi:
      return formatTvpi(value);
  }
}

export function formatPeriodLabel(date: number | string): string {
  return format(new Date(date), "MMM yyyy");
}

export type HistoryRow = DealReportingSnapshotItem & {
  moicGrossDeltaComputed?: number;
  irrNetDeltaComputed?: number;
  dpiDeltaComputed?: number;
  tvpiDeltaComputed?: number;
};

function computeDelta(
  current: number | null | undefined,
  prior: number | null | undefined,
): number | undefined {
  if (current == null || prior == null) return undefined;

  return current - prior;
}

export function computeHistoryDeltas(
  history: DealReportingSnapshotItem[],
): HistoryRow[] {
  const sorted = [...history].sort((a, b) =>
    compareDatesDesc(a.periodDate, b.periodDate),
  );

  return sorted.map((snapshot, index) => {
    const prior = sorted.at(index + 1);

    return {
      ...snapshot,
      moicGrossDeltaComputed: computeDelta(
        snapshot.moicGross,
        prior?.moicGross,
      ),
      irrNetDeltaComputed: computeDelta(snapshot.irrNet, prior?.irrNet),
      dpiDeltaComputed: computeDelta(snapshot.dpi, prior?.dpi),
      tvpiDeltaComputed: computeDelta(snapshot.tvpi, prior?.tvpi),
    };
  });
}

export function filterByTimeRange(
  points: MetricTrendPoint[],
  timeRange: TimeRange,
): MetricTrendPoint[] {
  if (timeRange === TimeRange.All) return points;
  const cutoff = subYears(new Date(), 1);

  return points.filter((pt) => new Date(pt.periodDate) >= cutoff);
}

export function getMetricTrend(
  data: DealFinancialReturnsResponse | undefined,
  metric: MetricKey,
): MetricTrendPoint[] {
  switch (metric) {
    case MetricKey.Moic:
      return data?.moicTrend ?? [];
    case MetricKey.Irr:
      return data?.irrTrend ?? [];
    case MetricKey.Dpi:
      return data?.dpiTrend ?? [];
    case MetricKey.Tvpi:
      return data?.tvpiTrend ?? [];
  }
}
